/**
 * Приоритеты, используются для задач. Модуль для работы с API
 * @module importances
 */

import { maxPerPage } from "@/constants";

/**
 * Получение списка приоритетов. Поддерживает пагинацию, поиск и фильтры
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список приоритетов
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/importances?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка приоритетов без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список приоритетов
 * @throws {Error} - ошибка при получении данных
 */
export const getImportanceOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/importances?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.items;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение приоритета по идентификатору
 * @param {String} uuid - идентификатор приоритета
 * @returns {Promise} - приоритет
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/importance?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание приоритета
 * @param {Object} item - данные приоритета
 * @returns {Promise} - созданный приоритет
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/importance", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление приоритета
 * @param {Object} item - данные приоритета
 * @returns {Promise} - обновленный приоритет
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/importance", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 *  Удаление приоритета
 * @param {Object} item - данные приоритета
 * @returns {Promise} - удаленный приоритет
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/importance", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};
